import { Fragment, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Input,
  Modal,
  Progress,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message
} from 'antd';
import { apiGet, apiPost } from '../api/client';
import { adminConfig, projectAdmin } from '../list-view/admin-config';
import { useLocalList } from '../list-view/use-local-list';
import { useAuthorizedSelection } from '../list-view/use-authorized-selection';
import { FilteredTable } from '../list-view/list-view';
import { detailConfig, projectProbation, projectTask, trackingConfig } from '../list-view/tracking-config';

type ProbationSummary = {
  outcomeAt?: string;
  nextOpenTaskDueDate?: string;
  id: string;
  kind?: 'PROBATION' | 'TRAINING_ONLY';
  offerId: string;
  materialId: string;
  employeeName: string;
  departmentName: string;
  position: string;
  employeeType?: string;
  jobLevel?: string;
  onboardDate: string;
  regularDate: string;
  probationEndDate?: string;
  regularDateEstimated?: boolean;
  source?: 'ONBOARDING' | 'DINGTALK_HRM';
  lastRosterSyncedAt?: string;
  status: string;
  hrbpName?: string;
  hrbpUserId?: string;
  reminderActivationStatus?: 'PENDING_REVIEW' | 'ENABLED';
  nextReminderDate?: string;
  todayReminderDue?: number;
  todayReminderSucceeded?: number;
  todayReminderFailed?: number;
  actualRegularDate?: string;
  appliedRegularDate?: string;
  regularizationSyncStatus?: 'PENDING' | 'SUCCEEDED' | 'FAILED';
  regularizationError?: string;
  outcomeReason?: string;
  totalTasks: number;
  completedTasks: number;
  overdueTasks: number;
  dueSoonTasks: number;
  progress: number;
  reminderMode: 'automatic';
};

type ProbationTask = {
  id: string;
  planId: string;
  key: string;
  title: string;
  owner: 'EMPLOYEE' | 'HR' | 'HRBP';
  reminderAudience: 'EMPLOYEE' | 'HR' | 'HRBP';
  recipientName?: string;
  resources?: ProbationTaskResource[];
  dueDate: string;
  status: 'PENDING' | 'REMINDER_SENT' | 'COMPLETED' | 'WAIVED';
  timingStatus: 'DONE' | 'OVERDUE' | 'DUE_SOON' | 'ON_TRACK';
  daysRemaining: number | null;
  lastRemindedAt?: string;
  completedAt?: string;
  completionSource?: 'HR_MANUAL' | 'DINGTALK_TODO';
  deferredToTaskKey?: string;
  deferredAt?: string;
  deferredBy?: string;
};

type ProbationTaskResource = {
  kind: 'TRAINING_VIDEO' | 'EXAM' | 'ANSWER_CARD';
  label: string;
  url: string;
};

type ProbationDelivery = {
  id: string;
  taskId: string;
  audience: 'EMPLOYEE' | 'HR' | 'HRBP';
  channels: string[];
  mode: 'mock' | 'live';
  status: string;
  source?: 'MANUAL' | 'AUTO';
  trigger?: 'MANUAL' | 'DUE_SOON' | 'DUE_TODAY' | 'OVERDUE';
  channelStatuses?: Record<string, string>;
  errorMessage?: string;
  createdAt: string;
};

type ProbationDetail = ProbationSummary & {
  tasks: ProbationTask[];
  deliveries: ProbationDelivery[];
};

type PlanStatus = 'ACTIVE' | 'REGULARIZED' | 'TERMINATED' | 'TRAINING_ONLY';

type ProductExamCandidate = {
  planId: string;
  taskId: string;
  employeeName: string;
  departmentName: string;
  group?: string;
  reason?: string;
  deliveryMode: 'NEW_TODO' | 'WORK_ONLY' | 'SKIP';
  proposedDueDate?: string;
  resources?: ProbationTaskResource[];
};

type ProductExamPreview = {
  previewToken: string;
  runtimeEnabled: boolean;
  rollout: { enabledAt?: string; verifiedAt?: string };
  candidates: ProductExamCandidate[];
};

type ProductExamPreflight = {
  valid: boolean;
  documents: Array<{ examUrl: string; expectedCardUrl: string; actualCardUrls: string[]; status: string }>;
};

export function ProbationTrackingPage() {
  const [planStatus, setPlanStatus] = useState<PlanStatus>('ACTIVE');
  const { items, loading, error, load } = useLocalList<ProbationSummary>(
    planStatus === 'TRAINING_ONLY'
      ? '/hr/probation?kind=TRAINING_ONLY'
      : planStatus === 'ACTIVE'
        ? '/hr/probation'
        : `/hr/probation?status=${planStatus}`
  );
  const [detail, setDetail] = useAuthorizedSelection<ProbationDetail>(items);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [syncLoading, setSyncLoading] = useState(false);
  const [pendingReminder, setPendingReminder] = useState<ProbationTask | null>(null);
  const [pendingDeferral, setPendingDeferral] = useState<ProbationTask | null>(null);
  const [pendingOutcome, setPendingOutcome] = useState<'REGULARIZED' | 'TERMINATED' | null>(null);
  const [actualRegularDate, setActualRegularDate] = useState('');
  const [pendingArchive, setPendingArchive] = useState<'REGULARIZED' | 'TERMINATED' | null>(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewCompletedTaskIds, setReviewCompletedTaskIds] = useState<string[]>([]);
  const [messageApi, holder] = message.useMessage();
  const [examPreview, setExamPreview] = useState<ProductExamPreview | null>(null);
  const [examCheck, setExamCheck] = useState<ProductExamPreflight | null>(null);
  const [examSelectedIds, setExamSelectedIds] = useState<string[]>([]);
  const [examLoading, setExamLoading] = useState('');
  const [examConfirmOpen, setExamConfirmOpen] = useState(false);
  const [examResults, setExamResults] = useState<
    Array<{ taskId: string; employeeName: string; status: string; reason?: string }>
  >([]);
  const examSendingRef = useRef(false);

  useEffect(() => {
    setPendingReminder(null);
    setPendingDeferral(null);
    setPendingOutcome(null);
    setPendingArchive(null);
    setReviewOpen(false);
  }, [detail?.id]);

  const syncDingTalk = async () => {
    setSyncLoading(true);
    try {
      const result = await apiPost<{ synced: number; created: number; updated: number }>('/hr/probation/sync-dingtalk');
      await load();
      messageApi.success(`已同步 ${result.synced} 名钉钉正式试用员工`);
    } catch (nextError) {
      messageApi.error((nextError as Error).message);
    } finally {
      setSyncLoading(false);
    }
  };

  const loadExamPreview = async () => {
    setExamLoading('preview');
    try {
      const preview = await apiGet<ProductExamPreview>('/hr/probation/product-exam/preview');
      setExamPreview(preview);
      setExamSelectedIds([]);
      setExamResults([]);
    } catch (nextError) {
      messageApi.error((nextError as Error).message);
    } finally {
      setExamLoading('');
    }
  };

  const preflightExam = async () => {
    setExamLoading('preflight');
    try {
      const check = await apiPost<ProductExamPreflight>('/hr/probation/product-exam/preflight');
      setExamCheck(check);
      setExamPreview(await apiGet<ProductExamPreview>('/hr/probation/product-exam/preview'));
      if (!check.valid) messageApi.error('答题卡链接不匹配或文档不可读，已阻止发布');
    } catch (nextError) {
      setExamCheck(null);
      messageApi.error((nextError as Error).message);
    } finally {
      setExamLoading('');
    }
  };

  const enableExam = async () => {
    setExamLoading('enable');
    try {
      await apiPost('/hr/probation/product-exam/enable', { confirmed: true });
      setExamPreview(await apiGet<ProductExamPreview>('/hr/probation/product-exam/preview'));
      messageApi.success('考试发布已启用；尚未向员工发送');
    } catch (nextError) {
      messageApi.error((nextError as Error).message);
    } finally {
      setExamLoading('');
    }
  };

  const sendExamBackfill = async () => {
    if (!examPreview || examLoading || !examSelectedIds.length || examSendingRef.current) return;
    examSendingRef.current = true;
    setExamLoading('send');
    try {
      const result = await apiPost<{ results: Array<{ taskId: string; status: string; reason?: string }> }>(
        '/hr/probation/product-exam/backfill',
        { taskIds: examSelectedIds, previewToken: examPreview.previewToken, confirmed: true }
      );
      setExamResults(
        result.results.map((item) => ({
          ...item,
          employeeName:
            examPreview.candidates.find((candidate) => candidate.taskId === item.taskId)?.employeeName ?? item.taskId
        }))
      );
      setExamConfirmOpen(false);
      setExamPreview(await apiGet<ProductExamPreview>('/hr/probation/product-exam/preview'));
      setExamSelectedIds([]);
      messageApi.info('补发批次已处理，请逐人核对发送结果');
    } catch (nextError) {
      messageApi.error((nextError as Error).message);
    } finally {
      examSendingRef.current = false;
      setExamLoading('');
    }
  };

  const openDetail = async (item: ProbationSummary) => {
    setDetailLoading(true);
    try {
      setDetail(await apiGet<ProbationDetail>(`/hr/probation/${item.id}`));
    } catch (nextError) {
      messageApi.error((nextError as Error).message);
    } finally {
      setDetailLoading(false);
    }
  };

  const executeTaskAction = async (task: ProbationTask, action: 'remind' | 'complete' | 'reopen' | 'defer') => {
    if (!detail) return;
    const loadingKey = `${task.id}:${action}`;
    setActionLoading(loadingKey);
    try {
      const result = await apiPost<{ delivery?: { channels?: string[] } }>(
        `/hr/probation/${detail.id}/tasks/${encodeURIComponent(task.id)}/${action}`,
        action === 'complete'
          ? { source: 'HR_MANUAL' }
          : action === 'remind' || action === 'defer'
            ? { confirmed: true }
            : undefined
      );
      setDetail(await apiGet<ProbationDetail>(`/hr/probation/${detail.id}`));
      await load();
      messageApi.success(
        action === 'remind'
          ? result.delivery?.channels?.includes('TODO')
            ? '工作通知和待办已发送'
            : '工作通知已发送，未新建待办'
          : action === 'complete'
            ? '任务已标记完成'
            : action === 'defer'
              ? '已延期至六个月答辩'
              : '任务已重新打开'
      );
    } catch (nextError) {
      messageApi.error((nextError as Error).message);
    } finally {
      setActionLoading('');
    }
  };

  const runTaskAction = async (task: ProbationTask, action: 'remind' | 'complete' | 'reopen' | 'defer') => {
    if (action === 'defer') {
      setPendingDeferral(task);
      return;
    }
    if (action !== 'remind' || !detail) return executeTaskAction(task, action);
    const recipient =
      task.recipientName ??
      (task.reminderAudience === 'HR'
        ? '宋述（susu）'
        : task.reminderAudience === 'HRBP'
          ? '未匹配 HRBP'
          : detail.employeeName);
    const dueText =
      !task.dueDate || task.daysRemaining === null
        ? '未设置截止日期'
        : task.daysRemaining < 0
          ? `已逾期 ${Math.abs(task.daysRemaining)} 天`
          : task.daysRemaining > 0
            ? `距离截止还有 ${task.daysRemaining} 天`
            : '今天截止';
    setPendingReminder({ ...task, reminderRecipient: recipient, reminderDueText: dueText } as ProbationTask);
  };

  const confirmArchive = async () => {
    if (!detail || !pendingArchive || archiveLoading) return;
    setArchiveLoading(true);
    try {
      await apiPost(`/hr/probation/${detail.id}/archive-outcome`, {
        confirmed: true,
        outcome: pendingArchive,
        reason: pendingArchive === 'REGULARIZED' ? 'HR确认已办理转正，仅归档' : 'HR确认试用期已离职，仅归档'
      });
      setPendingArchive(null);
      setDetail(null);
      await load();
      messageApi.success('人员结论已归档，历史记录已保留');
    } catch (nextError) {
      messageApi.error((nextError as Error).message);
    } finally {
      setArchiveLoading(false);
    }
  };

  const setOutcome = (outcome: 'REGULARIZED' | 'TERMINATED') => {
    if (!detail) return;
    setActualRegularDate('');
    setPendingOutcome(outcome);
  };

  const confirmOutcome = async () => {
    if (!detail || !pendingOutcome) return;
    const regularized = pendingOutcome === 'REGULARIZED';
    await apiPost(`/hr/probation/${detail.id}/outcome`, {
      confirmed: true,
      outcome: pendingOutcome,
      reason: regularized ? 'HR确认已转正' : 'HR确认已结束录用',
      actualRegularDate: regularized ? actualRegularDate : undefined
    });
    setPendingOutcome(null);
    setDetail(null);
    await load();
    messageApi.success(regularized ? '已移入已转正列表' : '已移入已离职列表');
  };

  const openReview = () => {
    if (!detail) return;
    setReviewCompletedTaskIds(detail.tasks.filter((task) => task.status === 'COMPLETED').map((task) => task.id));
    setReviewOpen(true);
  };

  const confirmReview = async () => {
    if (!detail) return;
    setActionLoading('activate-reminders');
    try {
      await apiPost(`/hr/probation/${detail.id}/activate-reminders`, {
        confirmed: true,
        completedTaskIds: reviewCompletedTaskIds
      });
      setReviewOpen(false);
      setDetail(await apiGet<ProbationDetail>(`/hr/probation/${detail.id}`));
      await load();
      messageApi.success('已完成核对，后续按每天 10:30 的规则自动提醒');
    } catch (nextError) {
      messageApi.error((nextError as Error).message);
    } finally {
      setActionLoading('');
    }
  };

  return (
    <section className="probation-page">
      {holder}
      <div className="probation-page-heading">
        <div>
          <Space align="center" wrap>
            <Typography.Title level={2}>试用期追踪</Typography.Title>
            <Tag color="blue">
              {planStatus === 'TRAINING_ONLY'
                ? '学习任务'
                : planStatus === 'ACTIVE'
                  ? '自动提醒 · 每天 10:30'
                  : '已归档 · 提醒已停止'}
            </Tag>
          </Space>
          <Typography.Text type="secondary">
            {planStatus === 'ACTIVE' || planStatus === 'TRAINING_ONLY'
              ? '历史人员核对后启用；页面刷新不会调用钉钉'
              : '历史任务与学习进度保留；页面刷新不会调用钉钉'}
          </Typography.Text>
        </div>
        <Space wrap>
          <Button type="primary" onClick={() => void syncDingTalk()} loading={syncLoading}>
            同步钉钉正式试用员工
          </Button>
          <Button onClick={() => void load()} loading={loading}>
            刷新
          </Button>
        </Space>
      </div>

      <Tabs
        activeKey={planStatus}
        onChange={(key) => setPlanStatus(key as PlanStatus)}
        items={[
          { key: 'ACTIVE', label: '试用中' },
          { key: 'REGULARIZED', label: '已转正' },
          { key: 'TERMINATED', label: '试用期离职' },
          { key: 'TRAINING_ONLY', label: '实习生学习' }
        ]}
      />

      {planStatus === 'TRAINING_ONLY' && (
        <Alert
          type="info"
          showIcon
          message="已到岗实习生同样需要学习新人视频；实习学习不包含试用期考核、转正或答辩，发送钉钉待办需逐项确认。"
        />
      )}

      <section aria-label="新人业务体验考试补发" className="probation-exam-backfill">
        <Space wrap>
          <Typography.Title level={4}>新人业务体验考试</Typography.Title>
          <Button onClick={() => void loadExamPreview()} loading={examLoading === 'preview'}>
            预览考试补发
          </Button>
          {examPreview && (
            <Button onClick={() => void preflightExam()} loading={examLoading === 'preflight'}>
              核验考试链接
            </Button>
          )}
          {examCheck?.valid && !examPreview?.rollout.enabledAt && (
            <Button onClick={() => void enableExam()} loading={examLoading === 'enable'}>
              确认启用考试发布
            </Button>
          )}
        </Space>
        {examPreview && (
          <Space direction="vertical" className="full">
            <Alert
              type={examPreview.rollout.enabledAt && examPreview.runtimeEnabled ? 'success' : 'warning'}
              showIcon
              message={
                examPreview.rollout.enabledAt && examPreview.runtimeEnabled
                  ? '发布已启用；仍须逐批确认才会发送'
                  : '发布尚未完全启用；预览不会发送待办或工作通知'
              }
            />
            {examCheck && !examCheck.valid && (
              <Alert type="error" showIcon message="答题卡链接不匹配或文档不可读；请先修正文档再核验" />
            )}
            <Typography.Text type="warning">原答题卡包含标准答案截图，发送前请确认员工可访问范围。</Typography.Text>
            <Table<ProductExamCandidate>
              rowKey="taskId"
              dataSource={examPreview.candidates}
              pagination={{ pageSize: 10 }}
              rowSelection={{
                selectedRowKeys: examSelectedIds,
                onChange: (keys) => setExamSelectedIds(keys.map(String).slice(0, 30)),
                getCheckboxProps: (candidate) => ({ disabled: candidate.deliveryMode === 'SKIP' })
              }}
              columns={[
                { title: '员工', dataIndex: 'employeeName' },
                { title: '部门', dataIndex: 'departmentName' },
                { title: '分组', dataIndex: 'group', render: (value) => value || '-' },
                {
                  title: '渠道',
                  dataIndex: 'deliveryMode',
                  render: (value) =>
                    value === 'NEW_TODO' ? '首次待办＋工作通知' : value === 'WORK_ONLY' ? '仅工作通知' : '跳过'
                },
                { title: '截止日', dataIndex: 'proposedDueDate', render: (value) => value || '原日期不变' },
                { title: '原因', dataIndex: 'reason', render: (value) => value || '-' },
                {
                  title: '资料',
                  dataIndex: 'resources',
                  render: (value) => <TaskResources resources={value} emptyText="-" />
                }
              ]}
            />
            <Button
              type="primary"
              disabled={
                !examCheck?.valid ||
                !examPreview.rollout.enabledAt ||
                !examPreview.runtimeEnabled ||
                examSelectedIds.length === 0 ||
                Boolean(examLoading)
              }
              onClick={() => setExamConfirmOpen(true)}
            >
              确认补发
            </Button>
            {examResults.length > 0 && (
              <Alert
                type="info"
                showIcon
                message={`本批结果：${examResults.map((item) => `${item.employeeName} ${item.status}${item.reason ? `（${item.reason}）` : ''}`).join('；')}`}
              />
            )}
          </Space>
        )}
      </section>

      {error && <Button onClick={() => void load()}>重新加载</Button>}
      <FilteredTable<ProbationSummary>
        config={trackingConfig(
          (
            { ACTIVE: 'active', REGULARIZED: 'regularized', TERMINATED: 'terminated', TRAINING_ONLY: 'intern' } as const
          )[planStatus]
        )}
        project={projectProbation}
        error={error}
        renderSummary={(rows) => <ProbationMetrics items={rows} planStatus={planStatus} />}
        className="data-table probation-people-table"
        rowKey="id"
        loading={loading}
        dataSource={items}
        tableLayout="fixed"
        scroll={{ x: 1480 }}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        locale={{
          emptyText: error
            ? '数据加载失败'
            : planStatus === 'TRAINING_ONLY'
              ? '当前没有已到岗实习生学习计划'
              : '当前没有试用期人员'
        }}
        columns={[
          {
            title: '员工',
            dataIndex: 'employeeName',
            width: 130,
            render: (value, item) => (
              <Space direction="vertical" size={2}>
                <Typography.Text strong>{value}</Typography.Text>
                {item.source === 'DINGTALK_HRM' && <Tag color="blue">钉钉花名册</Tag>}
              </Space>
            )
          },
          { title: '部门', dataIndex: 'departmentName', width: 180 },
          { title: '职位', dataIndex: 'position', width: 180 },
          { title: '员工类型', dataIndex: 'employeeType', width: 100 },
          { title: '职级', dataIndex: 'jobLevel', width: 80, render: (value) => value || '-' },
          {
            title: 'HRBP',
            dataIndex: 'hrbpName',
            width: 100,
            render: (value, item) => (item.kind === 'TRAINING_ONLY' ? '不适用' : value || <Tag color="red">待配置</Tag>)
          },
          { title: '入职日期', dataIndex: 'onboardDate', width: 110 },
          {
            title: '计划转正',
            dataIndex: 'regularDate',
            width: 120,
            render: (value, item) =>
              item.kind === 'TRAINING_ONLY' ? (
                '不适用'
              ) : (
                <Space direction="vertical" size={2}>
                  <span>{value}</span>
                  {item.regularDateEstimated && <Typography.Text type="secondary">按 6 个月推算</Typography.Text>}
                </Space>
              )
          },
          {
            title: '自动提醒',
            width: 130,
            render: (_, item) =>
              item.kind === 'TRAINING_ONLY' ? (
                <Tag>确认后发送</Tag>
              ) : item.status !== 'ACTIVE' ? (
                <Tag>已停止</Tag>
              ) : item.reminderActivationStatus === 'ENABLED' ? (
                <Space direction="vertical" size={2}>
                  <Tag color="green">已启用</Tag>
                  <Typography.Text type="secondary">{item.nextReminderDate || '暂无待提醒'}</Typography.Text>
                </Space>
              ) : (
                <Tag color="gold">待核对</Tag>
              )
          },
          {
            title: '任务进度',
            width: 190,
            render: (_, item) => (
              <div className="probation-progress-cell">
                <Progress percent={item.progress} size="small" />
                <Typography.Text type="secondary">
                  {item.completedTasks}/{item.totalTasks} 项
                </Typography.Text>
              </div>
            )
          },
          {
            title: '风险',
            width: 150,
            render: (_, item) => <RiskTags item={item} />
          },
          {
            title: '操作',
            width: 110,
            fixed: 'right',
            render: (_, item) => (
              <Button loading={detailLoading} onClick={() => void openDetail(item)}>
                查看任务
              </Button>
            )
          }
        ]}
      />

      <Fragment key={detail?.id || 'closed-task-dialogs'}>
        <Drawer
          key={detail?.id || 'closed-detail'}
          title={
            detail
              ? `${detail.employeeName}的${detail.kind === 'TRAINING_ONLY' ? '新人学习' : '试用期'}任务`
              : '试用期任务'
          }
          open={Boolean(detail)}
          onClose={() => setDetail(null)}
          width="min(820px, 100vw)"
          destroyOnClose
        >
          {detail && (
            <Space direction="vertical" size={20} className="full">
              <div className="probation-detail-summary">
                <span>{detail.departmentName}</span>
                <span>{detail.position}</span>
                <span>{detail.onboardDate} 入职</span>
                {detail.kind !== 'TRAINING_ONLY' && (
                  <>
                    <span>{detail.probationEndDate} 试用期结束</span>
                    <span>{detail.regularDate} 应转正</span>
                    <span>HRBP：{detail.hrbpName || '未匹配'}</span>
                  </>
                )}
              </div>
              {detail.kind !== 'TRAINING_ONLY' &&
                detail.status === 'ACTIVE' &&
                detail.reminderActivationStatus !== 'ENABLED' && (
                  <Alert
                    type="warning"
                    showIcon
                    message="该员工仍在历史任务核对阶段，系统不会自动发送提醒"
                    action={
                      <Button type="primary" onClick={openReview}>
                        完成核对并启用自动提醒
                      </Button>
                    }
                  />
                )}
              {detail.kind !== 'TRAINING_ONLY' && detail.status === 'ACTIVE' && (
                <Space wrap>
                  <Button type="primary" onClick={() => setOutcome('REGULARIZED')}>
                    标记已转正
                  </Button>
                  <Button onClick={() => setPendingArchive('REGULARIZED')}>已转正，仅归档</Button>
                  <Button danger onClick={() => setPendingArchive('TERMINATED')}>
                    已离职，仅归档
                  </Button>
                </Space>
              )}
              <Tabs
                items={[
                  {
                    key: 'employee',
                    label: `员工任务 (${detail.tasks.filter((task) => task.owner === 'EMPLOYEE').length})`,
                    children: (
                      <TaskTable
                        tasks={detail.tasks.filter((task) => task.owner === 'EMPLOYEE')}
                        actionLoading={actionLoading}
                        onAction={runTaskAction}
                      />
                    )
                  },
                  {
                    key: 'hr',
                    label: `HR 任务 (${detail.tasks.filter((task) => task.owner === 'HR').length})`,
                    children: (
                      <TaskTable
                        tasks={detail.tasks.filter((task) => task.owner === 'HR')}
                        actionLoading={actionLoading}
                        onAction={runTaskAction}
                      />
                    )
                  },
                  {
                    key: 'hrbp',
                    label: `HRBP 访谈 (${detail.tasks.filter((task) => task.owner === 'HRBP').length})`,
                    children: (
                      <TaskTable
                        tasks={detail.tasks.filter((task) => task.owner === 'HRBP')}
                        actionLoading={actionLoading}
                        onAction={runTaskAction}
                      />
                    )
                  },
                  {
                    key: 'history',
                    label: `提醒记录 (${detail.deliveries.length})`,
                    children: <DeliveryTable deliveries={detail.deliveries} />
                  }
                ].filter((item) => detail.kind !== 'TRAINING_ONLY' || !['hr', 'hrbp'].includes(item.key))}
              />
            </Space>
          )}
        </Drawer>

        <Modal
          title="确认考试补发名单"
          open={examConfirmOpen}
          okText="确认发送"
          okButtonProps={{ disabled: !examCheck?.valid || examLoading === 'send' }}
          confirmLoading={examLoading === 'send'}
          onCancel={() => setExamConfirmOpen(false)}
          onOk={() => void sendExamBackfill()}
        >
          <Typography.Text type="warning">答题卡原件包含标准答案；确认后将按以下对象与渠道发送。</Typography.Text>
          <Space direction="vertical">
            {examPreview?.candidates
              .filter((item) => examSelectedIds.includes(item.taskId))
              .map((item) => (
                <div key={item.taskId}>
                  {item.employeeName}：{item.deliveryMode === 'NEW_TODO' ? '首次待办＋工作通知' : '仅工作通知'}；截止{' '}
                  {item.proposedDueDate || '原日期不变'}
                  <TaskResources resources={item.resources} />
                </div>
              ))}
          </Space>
        </Modal>

        <Modal
          rootClassName="probation-confirmation-modal"
          zIndex={1300}
          title={pendingReminder?.key === 'employee-training-videos' ? '确认发送新人学习视频' : '确认发送试用期提醒'}
          open={Boolean(detail && pendingReminder)}
          okText="确认发送"
          cancelText="取消"
          confirmLoading={Boolean(pendingReminder && actionLoading === `${pendingReminder.id}:remind`)}
          onCancel={() => setPendingReminder(null)}
          onOk={async () => {
            if (!pendingReminder) return;
            await executeTaskAction(pendingReminder, 'remind');
            setPendingReminder(null);
          }}
        >
          {pendingReminder && (
            <Space direction="vertical">
              <span>
                发送给：{String((pendingReminder as ProbationTask & { reminderRecipient?: string }).reminderRecipient)}
              </span>
              <span>
                {pendingReminder.title}，
                {String((pendingReminder as ProbationTask & { reminderDueText?: string }).reminderDueText)}
              </span>
              <TaskResources resources={pendingReminder.resources} />
            </Space>
          )}
        </Modal>

        <Modal
          rootClassName="probation-confirmation-modal"
          zIndex={1300}
          title="确认延期至六个月答辩"
          open={Boolean(detail && pendingDeferral)}
          okText="确认延期"
          cancelText="取消"
          confirmLoading={Boolean(pendingDeferral && actionLoading === `${pendingDeferral.id}:defer`)}
          onCancel={() => setPendingDeferral(null)}
          onOk={async () => {
            if (!pendingDeferral) return;
            await executeTaskAction(pendingDeferral, 'defer');
            setPendingDeferral(null);
          }}
        >
          <Space direction="vertical">
            <span>三个月转正答辩将标记为已延期，并停止该任务的后续提醒。</span>
            <Typography.Text type="secondary">
              六个月转正答辩仍按原截止日期执行；此操作不会改变应转正日期或员工试用期状态。
            </Typography.Text>
          </Space>
        </Modal>

        <Modal
          rootClassName="probation-confirmation-modal"
          zIndex={1300}
          title="核对历史员工任务进度"
          open={Boolean(detail && reviewOpen)}
          okText="确认启用"
          cancelText="取消"
          confirmLoading={actionLoading === 'activate-reminders'}
          onCancel={() => setReviewOpen(false)}
          onOk={() => void confirmReview()}
        >
          {detail && (
            <Space direction="vertical" className="full">
              <Typography.Text type="secondary">
                勾选已经完成的项目。有截止日期的未完成项目启用后会按到期前 3
                天、到期当天和逾期每天提醒；未设置期限的学习任务不自动催办。
              </Typography.Text>
              <Checkbox.Group
                value={reviewCompletedTaskIds}
                onChange={(values) => setReviewCompletedTaskIds(values.map(String))}
              >
                <Space direction="vertical">
                  {detail.tasks.map((task) => (
                    <Checkbox key={task.id} value={task.id}>
                      {task.title}（{audienceLabel(task.reminderAudience)}，
                      {task.dueDate ? `截止 ${task.dueDate}` : '未设置截止日期'}）
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </Space>
          )}
        </Modal>

        <Modal
          rootClassName="probation-confirmation-modal"
          zIndex={1300}
          title="确认归档已办理的人员结论"
          open={Boolean(detail && pendingArchive)}
          okText="确认归档"
          cancelText="取消"
          confirmLoading={archiveLoading}
          closable={!archiveLoading}
          cancelButtonProps={{ disabled: archiveLoading }}
          onCancel={() => !archiveLoading && setPendingArchive(null)}
          onOk={() => void confirmArchive()}
        >
          <p>
            {detail?.employeeName}将从试用中移入{pendingArchive === 'REGULARIZED' ? '已转正' : '试用期离职'}。
          </p>
          <p>仅归档已确认的人员结论，不会调用钉钉办理转正或离职。历史任务及学习进度保留，后续试用期提醒停止。</p>
        </Modal>
        <Modal
          rootClassName="probation-confirmation-modal"
          zIndex={1300}
          title={pendingOutcome === 'REGULARIZED' ? '确认标记员工已转正' : '确认结束录用'}
          open={Boolean(detail && pendingOutcome)}
          okText={pendingOutcome === 'REGULARIZED' ? '确认已转正' : '确认已离职'}
          cancelText="取消"
          okButtonProps={
            pendingOutcome === 'TERMINATED'
              ? { danger: true }
              : { disabled: pendingOutcome === 'REGULARIZED' && !actualRegularDate }
          }
          onCancel={() => {
            setPendingOutcome(null);
            setActualRegularDate('');
          }}
          onOk={() => void confirmOutcome()}
        >
          {detail && pendingOutcome && (
            <Space direction="vertical" className="full">
              <span>
                {detail.employeeName}将从试用中移入{pendingOutcome === 'REGULARIZED' ? '已转正' : '已离职'}列表。
              </span>
              {pendingOutcome === 'REGULARIZED' && (
                <>
                  <Typography.Text type="secondary">
                    应转正日期：{detail.regularDate}。写入钉钉的日期不会晚于应转正日期。
                  </Typography.Text>
                  <Input
                    type="date"
                    placeholder="选择实际转正日期"
                    value={actualRegularDate}
                    onChange={(event) => setActualRegularDate(event.target.value)}
                  />
                </>
              )}
            </Space>
          )}
        </Modal>
      </Fragment>
    </section>
  );
}

function SummaryMetric({ label, value, tone }: { label: string; value: string | number; tone?: string }) {
  return (
    <div className={`probation-summary-metric ${tone ? `is-${tone}` : ''}`}>
      <Typography.Text type="secondary">{label}</Typography.Text>
      <strong>{value}</strong>
    </div>
  );
}

function RiskTags({ item }: { item: ProbationSummary }) {
  if (item.overdueTasks > 0) return <Tag color="red">逾期 {item.overdueTasks}</Tag>;
  if (item.dueSoonTasks > 0) return <Tag color="gold">临期 {item.dueSoonTasks}</Tag>;
  if (item.completedTasks === item.totalTasks) return <Tag color="green">全部完成</Tag>;
  return <Tag color="blue">正常推进</Tag>;
}

function TaskTable({
  tasks,
  actionLoading,
  onAction
}: {
  tasks: ProbationTask[];
  actionLoading: string;
  onAction: (task: ProbationTask, action: 'remind' | 'complete' | 'reopen' | 'defer') => Promise<void>;
}) {
  return (
    <FilteredTable<ProbationTask>
      config={detailConfig('tasks', `${tasks[0]?.planId ?? 'empty'}-${tasks[0]?.owner ?? ''}`)}
      project={projectTask}
      rowKey="id"
      dataSource={tasks}
      pagination={false}
      tableLayout="fixed"
      scroll={{ x: 1250 }}
      columns={[
        { title: '任务', dataIndex: 'title', width: 210 },
        {
          title: '相关资料',
          width: 190,
          render: (_, task) => <TaskResources resources={task.resources} emptyText="-" />
        },
        { title: '截止日期', dataIndex: 'dueDate', width: 110, render: (value) => value || '未设置' },
        {
          title: '状态',
          width: 110,
          render: (_, task) => <TaskStatusTag task={task} />
        },
        {
          title: '最近提醒',
          dataIndex: 'lastRemindedAt',
          width: 150,
          render: (value) => formatDateTime(value)
        },
        {
          title: '完成信息',
          width: 170,
          render: (_, task) =>
            task.status === 'COMPLETED' ? (
              <Space direction="vertical" size={2}>
                <Typography.Text>{completionSourceLabel(task)}</Typography.Text>
                <Typography.Text type="secondary">{formatDateTime(task.completedAt)}</Typography.Text>
              </Space>
            ) : (
              '-'
            )
        },
        {
          title: '操作',
          width: 330,
          fixed: 'right',
          render: (_, task) => (
            <Space wrap>
              {task.status !== 'COMPLETED' &&
                task.status !== 'WAIVED' &&
                task.key !== 'employee-product-experience' && (
                  <Button
                    size="small"
                    loading={actionLoading === `${task.id}:remind`}
                    onClick={() => void onAction(task, 'remind')}
                  >
                    发送提醒
                  </Button>
                )}
              {task.status === 'COMPLETED' ? (
                <Button
                  size="small"
                  loading={actionLoading === `${task.id}:reopen`}
                  onClick={() => void onAction(task, 'reopen')}
                >
                  重新打开
                </Button>
              ) : task.status === 'WAIVED' ? (
                <Button
                  size="small"
                  loading={actionLoading === `${task.id}:reopen`}
                  onClick={() => void onAction(task, 'reopen')}
                >
                  重新打开
                </Button>
              ) : (
                <>
                  {task.key === 'hr-defense-month-3' && (
                    <Button
                      size="small"
                      loading={actionLoading === `${task.id}:defer`}
                      onClick={() => void onAction(task, 'defer')}
                    >
                      延期至六个月答辩
                    </Button>
                  )}
                  <Button
                    size="small"
                    type="primary"
                    loading={actionLoading === `${task.id}:complete`}
                    onClick={() => void onAction(task, 'complete')}
                  >
                    标记完成
                  </Button>
                </>
              )}
            </Space>
          )
        }
      ]}
    />
  );
}

function TaskResources({ resources, emptyText }: { resources?: ProbationTaskResource[]; emptyText?: string }) {
  if (!resources?.length) return <span>{emptyText}</span>;
  return (
    <Space direction="vertical" size={2}>
      {resources.map((resource) => (
        <Typography.Link key={`${resource.kind}:${resource.url}`} href={resource.url} target="_blank" rel="noreferrer">
          {resource.label}
        </Typography.Link>
      ))}
    </Space>
  );
}

function DeliveryTable({ deliveries }: { deliveries: ProbationDelivery[] }) {
  return (
    <FilteredTable<ProbationDelivery>
      config={adminConfig('deliveries', deliveries[0]?.id ?? 'empty')}
      project={(row, index) => projectAdmin('deliveries', row, index)}
      rowKey="id"
      dataSource={deliveries}
      pagination={false}
      locale={{ emptyText: '尚未发送提醒' }}
      columns={[
        { title: '时间', dataIndex: 'createdAt', render: formatDateTime },
        { title: '对象', dataIndex: 'audience', render: audienceLabel },
        { title: '触发', dataIndex: 'trigger', render: reminderTriggerLabel },
        { title: '渠道', dataIndex: 'channels', render: (value: string[]) => value.join(' + ') },
        {
          title: '结果',
          dataIndex: 'status',
          render: (value, delivery) => (
            <Space direction="vertical" size={2}>
              <Tag color={value === 'FAILED' ? 'red' : 'blue'}>{value}</Tag>
              {delivery.errorMessage && <Typography.Text type="danger">{delivery.errorMessage}</Typography.Text>}
            </Space>
          )
        }
      ]}
    />
  );
}

function TaskStatusTag({ task }: { task: ProbationTask }) {
  if (task.status === 'COMPLETED') return <Tag color="green">已完成</Tag>;
  if (task.status === 'WAIVED' && task.deferredToTaskKey) return <Tag color="gold">已延期</Tag>;
  if (task.status === 'WAIVED') return <Tag>已豁免</Tag>;
  if (task.timingStatus === 'OVERDUE') return <Tag color="red">已逾期</Tag>;
  if (task.timingStatus === 'DUE_SOON') return <Tag color="gold">即将到期</Tag>;
  if (task.status === 'REMINDER_SENT') return <Tag color="blue">已提醒</Tag>;
  return <Tag>待处理</Tag>;
}

function ProbationMetrics({ items, planStatus }: { items: ProbationSummary[]; planStatus: PlanStatus }) {
  const totals = summarize(items);
  return (
    <div className="probation-summary-band">
      <SummaryMetric
        label={
          {
            ACTIVE: '试用期人员',
            REGULARIZED: '已转正人员',
            TERMINATED: '试用期离职人员',
            TRAINING_ONLY: '实习学习人员'
          }[planStatus]
        }
        value={totals.people}
      />
      <SummaryMetric label="整体进度" value={`${totals.progress}%`} />
      <SummaryMetric label="待核对" value={totals.pendingReview} tone="warning" />
      <SummaryMetric label="今日应提醒" value={totals.todayDue} />
      <SummaryMetric label="今日失败" value={totals.todayFailed} tone="danger" />
    </div>
  );
}

function summarize(items: ProbationSummary[]) {
  const totalTasks = items.reduce((sum, item) => sum + item.totalTasks, 0);
  const completedTasks = items.reduce((sum, item) => sum + item.completedTasks, 0);
  return {
    people: items.length,
    progress: totalTasks ? Math.round((completedTasks / totalTasks) * 100) : 0,
    dueSoon: items.reduce((sum, item) => sum + item.dueSoonTasks, 0),
    overdue: items.reduce((sum, item) => sum + item.overdueTasks, 0),
    pendingReview: items.filter((item) => item.status === 'ACTIVE' && item.reminderActivationStatus !== 'ENABLED')
      .length,
    todayDue: items.reduce((sum, item) => sum + (item.todayReminderDue ?? 0), 0),
    todayFailed: items.reduce((sum, item) => sum + (item.todayReminderFailed ?? 0), 0)
  };
}

function audienceLabel(value: string) {
  if (value === 'HR') return '宋述';
  if (value === 'HRBP') return 'HRBP';
  return '员工';
}

function reminderTriggerLabel(value?: string) {
  return (
    (
      {
        MANUAL: '手动',
        DUE_SOON: '到期前 3 天',
        DUE_TODAY: '到期当天',
        OVERDUE: '逾期'
      } as Record<string, string>
    )[String(value)] ?? '-'
  );
}

function completionSourceLabel(task: ProbationTask) {
  if (task.completionSource === 'DINGTALK_TODO') {
    if (task.owner === 'EMPLOYEE') return '员工在钉钉完成';
    return `${task.recipientName || audienceLabel(task.reminderAudience)}在钉钉完成`;
  }
  if (task.completionSource === 'HR_MANUAL') return 'HR 手动标记';
  return '已完成';
}

function formatDateTime(value?: string) {
  if (!value) return '-';
  return String(value).replace('T', ' ').slice(0, 16);
}
